import React, { useState } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Input } from '../components/common/Input';
import { Button } from '../components/common/Button';

const AuthContainer = styled.div`
  max-width: 400px;
  margin: 0 auto;
  padding: 24px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 48px;
`;

const AuthHeader = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const AuthTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #212529;
  margin: 0 0 8px 0;
`;

const AuthSubtitle = styled.p`
  font-size: 1rem;
  color: #495057;
  margin: 0;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Message = styled.div<{ type: 'success' | 'error' }>`
  color: ${props => props.type === 'success' ? '#51cf66' : '#fa5252'};
  background-color: ${props => props.type === 'success' ? '#f3fcf3' : '#fff5f5'};
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 0.875rem;
  line-height: 1.4;
`;

const PasswordRequirements = styled.div`
  background-color: #f8f9fa;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 0.875rem;
  color: #495057;
  
  ul {
    margin: 4px 0 0 0;
    padding-left: 16px;
  }
  
  li {
    margin: 2px 0;
  }
`;

const AuthFooter = styled.div`
  margin-top: 24px;
  text-align: center;
  font-size: 0.875rem;
  color: #495057;
  
  a {
    color: #4263eb;
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ResetPassword: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    
    // Validation
    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters long.' });
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match.' });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/reset-password/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newPassword })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ 
          type: 'success', 
          text: 'Password reset successfully! Redirecting to login...' 
        });
        
        // Redirect to login with success message
        setTimeout(() => {
          navigate('/login', { 
            state: { message: 'Password reset successfully! Please log in with your new password.' }
          });
        }, 2000);
      } else {
        setMessage({ 
          type: 'error', 
          text: data.error || 'Reset failed. Please try again or request a new reset link.' 
        });
      }

    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: 'An error occurred. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContainer>
      <AuthHeader>
        <AuthTitle>Reset Password</AuthTitle>
        <AuthSubtitle>
          Enter your new password below.
        </AuthSubtitle>
      </AuthHeader>
      
      {message && (
        <Message type={message.type}>
          {message.text}
        </Message>
      )}
      
      <PasswordRequirements>
        <strong>Password requirements:</strong>
        <ul>
          <li>At least 6 characters long</li>
          <li>Should be unique and not easily guessable</li>
        </ul>
      </PasswordRequirements>
      
      <Form onSubmit={handleSubmit}>
        <Input
          label="New Password"
          type="password"
          placeholder="Enter your new password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          minLength={6}
        />
        
        <Input
          label="Confirm New Password"
          type="password"
          placeholder="Confirm your new password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          minLength={6}
        />
        
        <Button
          type="submit"
          variant="primary"
          isFullWidth
          isLoading={loading}
          disabled={loading}
        >
          {loading ? 'Resetting...' : 'Reset Password'}
        </Button>
      </Form>
      
      <AuthFooter>
        <Link to="/login">Back to Login</Link>
      </AuthFooter>
    </AuthContainer>
  );
};

export default ResetPassword;